<template>
  <div
    v-if="showActionButtons"
    class="hidden tablet-h:flex items-center gap-[10px]"
  >
    <Button variant="stroke" :icon="MoveIcon" @click="handleMoveClick">
      Move
    </Button>
    <Button
      v-if="isSingleFileSelected"
      variant="stroke"
      class="hidden min-[1240px]:flex"
      :icon="ArchivedIcon"
      :disabled="isCoOwner || isAuthor"
      @click="handleArchiveClick"
    >
      Archive
    </Button>
    <Button
      v-if="areAllFolders"
      variant="stroke"
      :icon="TrashIcon"
      @click="handleDeleteClick"
    >
      Delete
    </Button>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@dacr/library'
import { MoveIcon, ArchivedIcon, TrashIcon } from '@dacr/icons'
import { FileRole, type Item } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'
import { computed } from 'vue'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

const props = defineProps<{
  selectedItems: Item[]
}>()

const emit = defineEmits<{
  reset: []
}>()

const showActionButtons = computed(() => props.selectedItems.length > 0)

const areAllFiles = computed(
  () => props.selectedItems.length > 0 && props.selectedItems.every(isFile)
)

const areAllFolders = computed(
  () => props.selectedItems.length > 0 && props.selectedItems.every(isFolder)
)

const isSingleFileSelected = computed(
  () => areAllFiles.value && props.selectedItems.length === 1
)

const isCoOwner = computed(() => {
  const first = props.selectedItems[0]
  return !!(first && isFile(first) && first.role === FileRole.CoOwner)
})

const isAuthor = computed(() => {
  const first = props.selectedItems[0]
  return !!(first && isFile(first) && first.role === FileRole.Author)
})

const handleMoveClick = () => {
  openDialog(Dialog.MoveItems, {
    props: { ids: props.selectedItems.map((item) => item.id) },
    events: {
      close() {
        emit('reset')
      },
    },
  })
}

const handleArchiveClick = () => {
  openModal(Modals.ArchiveFiles, {
    props: { files: props.selectedItems.filter(isFile) },
  })
}

const handleDeleteClick = () => {
  openModal(Modals.DeleteFolders, {
    props: { items: props.selectedItems },
  })
}
</script>
